<template>
	<div class="image-uploader">
        <div class="card">
            <div class="card-body">
                <div
                    class="drop-zone"
                    :class="{ 'drag-over': isDragging }"
                    @dragover.prevent="onDragOver"
                    @dragleave.prevent="onDragLeave"
                    @drop.prevent="onDrop"
                    @click="triggerFileInput"
                >
                    <div v-if="!isUploading && !files.length" class="text-center py-5">
                        <i class="bi bi-cloud-arrow-up display-4"></i>
                        <h5 class="mt-3">Drag and drop files here or click to select</h5>
                        <p v-if="multiple" class="text-muted small">You can upload up to {{ maxFiles }} files at once</p>
                    </div>

                    <div v-else-if="isUploading" class="upload-status p-3">
                        <h5>Uploading {{ uploadProgress.completed }} of {{ uploadProgress.total }} files</h5>
                        <div class="mb-3">
                            <div class="d-flex justify-content-between mb-1">
                                <span>Overall Progress</span>
                                <span>{{ uploadProgress.percentage.toFixed(0) }}%</span>
                            </div>
                            <div class="progress" style="height: 20px;">
                                <div
                                    class="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    :style="{ width: `${uploadProgress.percentage}%` }"
                                    :aria-valuenow="uploadProgress.percentage"
                                    aria-valuemin="0"
                                    aria-valuemax="100">
                                </div>
                            </div>
                        </div>

                        <!-- Individual file progress cards -->
                        <div class="row g-3">
                            <div v-for="(file, index) in uploadingFiles" :key="index" class="col-6 col-md-4 col-lg-3">
                                <div class="card h-100 file-progress-card">
                                    <div class="card-body p-2">
                                        <div class="image-preview mb-2">
                                            <img :src="file.preview" alt="Preview" class="img-fluid rounded" />
                                            <div class="progress-overlay d-flex align-items-center justify-content-center">
                                                <div class="progress-percentage">{{ file.progress.toFixed(0) }}%</div>
                                            </div>
                                        </div>
                                        <div class="text-truncate small mb-1">{{ file.name }}</div>
                                        <div class="progress" style="height: 8px;">
                                            <div
                                                class="progress-bar"
                                                role="progressbar"
                                                :class="{'bg-success': file.progress >= 100}"
                                                :style="{ width: `${file.progress}%` }"
                                                :aria-valuenow="file.progress"
                                                aria-valuemin="0"
                                                aria-valuemax="100">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="p-3">
                        <div class="row g-3 preview-container">
                            <div v-for="(file, index) in files" :key="index" class="col-6 col-sm-4 col-md-3">
                                <div class="preview-item card h-100 mb-0">
                                    <div class="image-wrapper position-relative">
                                        <img :src="prepareImageSource(file)" alt="Preview" class="card-img-top preview-image" />
                                        <span @click.stop="removeFile(index)" class="position-absolute top-0 start-100 translate-middle cursor-pointer">
                                            <i class="bi bi-x-circle-fill text-danger"></i>
                                        </span>
                                        <!-- <button
                                            @click.stop="removeFile(index)"
                                            class="btn btn-danger btn-sm position-absolute rounded-circle end-0 top-0 m-1"
                                            title="Remove image"
                                        >
                                            <i class="bi bi-x"></i>
                                        </button> -->
                                    </div>
                                    <div class="card-footer p-1 text-center">
                                        <small class="text-truncate d-block">{{ file.name }}</small>
                                        <!-- <small class="text-muted">{{ formatFileSize(file.size) }}</small> -->

                                        <!-- File type indicator -->
                                        <!-- <div class="mt-1">
                                            <span v-if="file.type.includes('image')" class="badge bg-success me-1">
                                                <i class="bi bi-image me-1"></i>Image
                                            </span>
                                            <span v-else-if="file.type.includes('pdf')" class="badge bg-danger me-1">
                                                <i class="bi bi-file-pdf me-1"></i>PDF
                                            </span>
                                            <span v-else-if="file.type.includes('msword') || file.type.includes('document')" class="badge bg-primary me-1">
                                                <i class="bi bi-file-word me-1"></i>DOC
                                            </span>
                                            <span v-else class="badge bg-secondary me-1">
                                                <i class="bi bi-file me-1"></i>File
                                            </span>
                                        </div> -->

                                        <!-- Compression status (for all files) -->
                                        <!-- <div v-if="file.compressed" class="mt-1">
                                            <small class="text-success">
                                                <i class="bi bi-check-circle me-1"></i>
                                                Compressed: {{ formatFileSize(file.originalSize) }} → {{ formatFileSize(file.size) }}
                                            </small>
                                            <br>
                                            <small class="text-muted">
                                                Saved {{ Math.round(((file.originalSize - file.size) / file.originalSize) * 100) }}%
                                            </small>
                                        </div> -->
                                    </div>
                                </div>
                            </div>

                            <div v-if="multiple" class="col-6 col-sm-4 col-md-3">
                                <div
                                    class="add-more card h-100 d-flex justify-content-center align-items-center"
                                    @click.stop="triggerFileInput"
                                >
                                    <i class="bi bi-plus-lg display-6"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <input
                    type="file"
                    ref="fileInput"
                    :multiple="multiple"
                    @change="onFileSelected"
                    class="file-input d-none"
                />
                <div v-if="isVideoCompressing" class="alert alert-info text-center my-3">
                    <Loader text="Optimizing video, please wait..." />
                </div>
            </div>
        </div>
	</div>
</template>

<script>
import Loader from '../partials/Loader.vue';
export default {
	name: 'ImageUploader',
    components: { Loader },
    emits: ['update:modelValue', 'filesChanged'],
    props: {
        orderId: {
            type: Number,
            default: 0
        },
        multiple: {
            type: Boolean,
            default: true
        },
        maxFiles: {
            type: Number,
            default: 20
        }
    },
	data() {
        return {
            isDragging: false,
            isVideoCompressing: false, // Track video compression state
            files: [],
            uploadedFiles: [],
            uploadingFiles: [], // Track files currently uploading with their progress
            isUploading: false,
            uploadProgress: {
                total: 0,
                completed: 0,
                percentage: 0,
                fileProgress: {}
            },
            chunkSize: 2 * 1024 * 1024, // 2MB chunks
            concurrentUploads: 3,
            maxFileSize: 512 * 1024 * 1024, // 512MB max file size
            urlCopied: false,
            copiedFileId: null,
            toastVisible: false,
            toastMessage: '',
            batchId: Date.now(),
            // Auto compression settings (no UI)
            autoCompress: true,
            compressionSettings: {
                imageQuality: 0.9, // 90% quality as requested
                imageMaxWidth: 1920, // Keep 1920x1080 resolution
                imageMaxHeight: 1080,
                imageFormat: 'image/webp', // WebP for better compression
                targetFileSize: 100 * 1024, // 100KB target
                enableMultiPass: true,
                enableSmartResize: true,
                maxIterations: 10,
                minQuality: 0.3,
                aggressiveMode: true,

                // UPDATED VIDEO COMPRESSION SETTINGS FOR BETTER PERFORMANCE
                videoQuality: 0.8, // 80% quality for videos
                videoMaxWidth: 1920,
                videoMaxHeight: 1080,
                videoFormat: 'video/webm', // WebM for better compression
                videoTargetFileSize: 5 * 1024 * 1024, // 5MB target for videos
                videoBitrate: 2000000, // INCREASED from 1000000 to 2000000 (2Mbps) for better quality/speed balance
                videoFrameRate: 30, // 30fps
                enableVideoMultiPass: false, // CHANGED from true to false for faster processing
                videoMaxIterations: 3, // REDUCED from 5 to 3 iterations
                videoMinQuality: 0.5, // INCREASED from 0.4 to 0.5 (higher minimum quality)
                
                // NEW PERFORMANCE SETTINGS (ADD THESE)
                useHardwareAcceleration: true,
                preferGPUProcessing: true,
                optimizeForSpeed: true,
                maxProcessingTime: 60000, // 1 minute timeout
                enableWebCodecs: true, // Enable WebCodecs API if available
                preferredCodec: 'vp9', // Prefer VP9 codec for better compression
                chunkProcessing: true, // Enable chunk-based processing
                backgroundProcessing: true // Allow background processing
           }
        };
	},
	methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
            this.batchId = Date.now();
        },

        onDragOver(e) {
            this.isDragging = true;
        },

        onDragLeave(e) {
            this.isDragging = false;
        },

        onDrop(e) {
            this.isDragging = false;
            
            //this.handleFiles(e.dataTransfer.files);

            // First check for regular files
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                this.handleFiles(e.dataTransfer.files);
                return;
            }

            // Check for items (needed for WhatsApp Web blob URLs)
            if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
                const items = Array.from(e.dataTransfer.items);
                for (const item of items) {
                    if (item.kind === 'file' && item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file) {
                            this.handleFiles([file]);
                            return;
                        }
                    }
                }
            }

            // Check for URL-based transfers (like from WhatsApp Web)
            const html = e.dataTransfer.getData('text/html');
            const text = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
            
            if (html || text) {
                // Extract image URL from HTML or plain text
                let imageUrl = null;
                
                if (html) {
                    // Look for img tags in HTML
                    const imgMatch = html.match(/<img[^>]+src="([^"]+)"/);
                    if (imgMatch) {
                        imageUrl = imgMatch[1];
                    }
                }
                console.log('Extracted imageUrl:', imageUrl);
                
                if (!imageUrl && text) {
                    // Check if text is a direct image URL
                    imageUrl = text.trim();
                }
                
                // Check if it's a blob URL - if so, we can't fetch it
                if (imageUrl && imageUrl.startsWith('blob:')) {
                    this.$notification('error', 'Images from WhatsApp Web cannot be uploaded directly. Please right-click the image in WhatsApp, select "Save image as", then upload the saved file.');
                    return;
                }
                
                if (imageUrl) {
                    this.fetchImageFromUrl(imageUrl);
                }
            }
        },
        async fetchImageFromUrl(url) {
            try {
                // Fetch the image from URL
                const response = await fetch(url);
                const blob = await response.blob();
                
                // Convert blob to File object
                const fileName = url.split('/').pop() || 'whatsapp-image.jpg';
                const file = new File([blob], fileName, { type: blob.type });
                
                // Handle the file
                this.handleFiles([file]);
            } catch (error) {
                console.error('Error fetching image from URL:', error);
                this.$notification('error', 'Unable to fetch image from WhatsApp Web. Please download the image first and then upload it.');
            }
        },

        onFileSelected(e) {
            this.handleFiles(e.target.files);
            // Reset the input so the same file can be selected again
            this.$refs.fileInput.value = '';
        },

        async handleFiles(fileList) {
            this.batchId = Date.now();

            // Check if adding these files would exceed max files
            if (this.files.length + fileList.length > this.maxFiles) {
                alert(`You can only upload up to ${this.maxFiles} files at once.`);
                return;
            }

            // Use for...of instead of forEach to handle async/await properly
            for (const file of Array.from(fileList)) {
                // Check supported file types
                const supportedTypes = [
                    'image/', 'application/pdf', 'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'text/', 'application/zip', 'video/'
                ];

                const isSupported = supportedTypes.some(type => file.type.startsWith(type));
                if (!isSupported) {
                    alert(`File "${file.name}" is not a supported file type.`);
                    continue;
                }

                // Check file size
                if (file.size > this.maxFileSize) {
                    alert(`File "${file.name}" exceeds the maximum size of ${this.formatFileSize(this.maxFileSize)}`);
                    continue;
                }

                // Auto-compress all files based on type
                let processedFile = file;
                let isCompressed = false;

                try {
                    if (file.type.startsWith('image/')) {
                        // Compress images using Canvas API
                        processedFile = await this.compressImage(file);
                        isCompressed = true;
                    } else if (file.type.startsWith('video/')) {
                        // Compress video files
                        processedFile = file; //await this.compressVideo(file);
                        isCompressed = true;
                    } else {
                        // For non-image, non-video files, apply generic compression
                        // processedFile = await this.compressGenericFile(file);
                        // isCompressed = processedFile.size < file.size;
                        processedFile = file;
                        isCompressed = false;
                    }

                    if (isCompressed) {
                        console.log(`Compressed ${file.name}: ${this.formatFileSize(file.size)} → ${this.formatFileSize(processedFile.size)}`);
                    }
                } catch (error) {
                    console.error('Compression failed, using original file:', error);
                    processedFile = file;
                    isCompressed = false;
                }

                // Create preview
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.files.push({
                        file: processedFile,
                        preview: e.target.result,
                        name: file.name, // Keep original name
                        size: processedFile.size,
                        type: processedFile.type,
                        progress: 0,
                        originalSize: file.size, // Track original size for comparison
                        compressed: isCompressed,
                        isImage: file.type.startsWith('image/'),
                        isPdf: file.type === 'application/pdf',
                        isDoc: file.type.includes('word') || file.type.includes('document')
                    });
                    
                    // Emit files to parent component
                    this.$emit('update:modelValue', this.files);
                    this.$emit('filesChanged', this.files);
                };
                reader.readAsDataURL(processedFile);
            }
        },

        removeFile(index) {
            this.files.splice(index, 1);
            
            // Emit updated files to parent
            this.$emit('update:modelValue', this.files);
            this.$emit('filesChanged', this.files);
        },

        clearFiles() {
            this.files = [];
            
            // Emit cleared files to parent
            this.$emit('update:modelValue', this.files);
            this.$emit('filesChanged', this.files);
        },

        clearUploadedFiles() {
            if (confirm('Are you sure you want to clear all uploaded files?')) {
                this.uploadedFiles = [];
            }
        },

        removeUploadedFile(index) {
            if (confirm('Are you sure you want to remove this file?')) {
            // Here you would typically make an API call to delete the file on the server
            // For now, we'll just remove it from the front-end
            this.uploadedFiles.splice(index, 1);
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';

            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        async uploadFiles() {
            if (!this.files.length) return;

            this.isUploading = true;
            this.uploadingFiles = [...this.files];
            this.uploadProgress = {
                total: this.files.length,
                completed: 0,
                percentage: 0,
                fileProgress: {}
            };

            // Create a queue of files to upload
            const queue = [...this.files];
            const activeUploads = new Set();

            // Process uploads
            const processQueue = async () => {
                if (queue.length === 0 && activeUploads.size === 0) {
                    // All uploads finished
                    this.isUploading = false;
                    this.files = [];
                    this.uploadingFiles = [];

                    // Display notification
                    this.$notification('success', 'All files uploaded successfully');

                    // Track image upload history
                    axios.post('/api/handyman/uploads/generate-history', {
                        order_id: this.orderId,
                        batch_id: this.batchId,
                        action: 'generate_history'
                    })
                    .then(response => {
                        // Emit event to notify parent that all uploads are complete
                        this.$emit('uploads-completed', this.uploadedFiles);
                    })
                    .catch(error => {
                        console.log(error);
                    });

                    // Reset batch ID
                    this.batchId = Date.now();

                    return;
                }

                // Start new uploads if there's capacity and files in the queue
                while (queue.length > 0 && activeUploads.size < this.concurrentUploads) {
                    const fileData = queue.shift();
                    activeUploads.add(fileData);

                    this.uploadFile(fileData)
                        .then(response => {
                            // Add to uploaded files
                            this.uploadedFiles.push({
                                name: fileData.name,
                                url: response.url,
                                id: response.id
                            });

                            this.uploadProgress.completed++;
                            this.uploadProgress.percentage = (this.uploadProgress.completed / this.uploadProgress.total) * 100;

                            // Remove from active uploads
                            activeUploads.delete(fileData);

                            // Continue processing queue
                            processQueue();
                        })
                        .catch(error => {
                            console.error(`Error uploading ${fileData.name}:`, error);
                            // Show more detailed error message
                            alert(`Failed to upload ${fileData.name}: ${error.message || 'Unknown error'}`);

                            this.uploadProgress.completed++;
                            this.uploadProgress.percentage = (this.uploadProgress.completed / this.uploadProgress.total) * 100;

                            // Remove from active uploads
                            activeUploads.delete(fileData);

                            // Continue processing queue
                            processQueue();
                        });
                }
            };

            // Start processing
            processQueue();
        },

        async uploadFile(fileData) {
            const file = fileData.file;
            const totalChunks = Math.ceil(file.size / this.chunkSize);

            try {
                // Initialize file upload on the server
                const initResponse = await axios.post('/api/handyman/uploads/init', {
                    filename: file.name,
                    filesize: file.size,
                    type: file.type,
                    chunks: totalChunks
                });

                const fileId = initResponse.data.file_id;

                // Find the index of this file in the uploadingFiles array
                const fileIndex = this.uploadingFiles.findIndex(f =>
                    f.name === fileData.name && f.size === fileData.size
                );

                // Set initial progress - use direct property assignment instead of $set
                if (fileIndex !== -1) {
                    this.uploadingFiles[fileIndex].progress = 0;
                }

                // Upload each chunk
                for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                    const start = chunkIndex * this.chunkSize;
                    const end = Math.min(start + this.chunkSize, file.size);
                    const chunk = file.slice(start, end);

                    const formData = new FormData();
                    formData.append('file', chunk);
                    formData.append('file_id', fileId);
                    formData.append('chunk_index', chunkIndex);
                    formData.append('total_chunks', totalChunks);

                    // Store reference to component instance
                    const self = this;
                    const currentFileIndex = fileIndex;

                    try {
                        await axios.post('/api/handyman/uploads/chunk', formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            },
                            onUploadProgress: function(progressEvent) {
                                // Calculate the chunk progress
                                const chunkProgress = (progressEvent.loaded / progressEvent.total) * 100;

                                // Calculate overall file progress based on chunks
                                const fileProgress = ((chunkIndex + (chunkProgress / 100)) / totalChunks) * 100;

                                // Update progress in the uploadingFiles array - use direct property assignment
                                if (currentFileIndex !== -1 && self.uploadingFiles[currentFileIndex]) {
                                    self.uploadingFiles[currentFileIndex].progress = fileProgress;
                                }
                            }
                        });

                        // Update progress after each chunk is complete
                        if (fileIndex !== -1 && this.uploadingFiles[fileIndex]) {
                            const completedChunkProgress = ((chunkIndex + 1) / totalChunks) * 100;
                            this.uploadingFiles[fileIndex].progress = completedChunkProgress;
                        }
                    } catch (chunkError) {
                        console.error(`Error uploading chunk ${chunkIndex} for ${file.name}:`, chunkError);
                        throw new Error(`Failed to upload chunk ${chunkIndex}: ${chunkError.response?.data?.message || chunkError.message}`);
                    }
                }

                // Complete the file upload
                try {
                    const finalizeResponse = await axios.post('/api/handyman/uploads/finalize', {
                        file_id: fileId,
                        order_id: this.orderId,
                        batch_id: this.batchId
                    });

                    // Set progress to 100% when complete
                    if (fileIndex !== -1 && this.uploadingFiles[fileIndex]) {
                        this.uploadingFiles[fileIndex].progress = 100;
                    }

                    return {
                        url: finalizeResponse.data.url,
                        id: finalizeResponse.data.id
                    };
                } catch (finalizeError) {
                    console.error(`Error finalizing upload for ${file.name}:`, finalizeError);
                    throw new Error(`Failed to finalize upload: ${finalizeError.response?.data?.message || finalizeError.message}`);
                }
            } catch (error) {
                console.error(`Complete error details for ${file.name}:`, error);
                // Add more detailed error information to the alert
                const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
                throw new Error(`Failed to upload ${file.name}: ${errorMessage}`);
            }
        },

        // Prepare image source for display
        prepareImageSource(item) {
            if (item.type.includes('image')) {
                return item.preview;
            }
            else if(item.type.includes('video')) {
                return this.$baseUrl + "/images/svg/file-video.svg";
            }
            else if(item.type.includes('pdf')) {
                return this.$baseUrl + "/images/svg/file-pdf.svg";
            }
            else if(item.type.includes('msword') || item.type.includes('document')) {
                return this.$baseUrl + "/images/svg/file-text.svg";
            }
            else if(item.type.includes('zip')) {
                return this.$baseUrl + "/images/svg/file-archive.svg";
            }
            else {
                return this.$baseUrl + "/images/svg/file-text.svg";
            }
        },
        /**
         * Compress generic files (PDF, DOC, etc.) using browser compression
         * @param {File} file - Original file
         * @returns {Promise<File>} - Compressed file
         */
        async compressGenericFile(file) {
            try {
                // Read file as array buffer
                const arrayBuffer = await file.arrayBuffer();

                // Use CompressionStream if available (modern browsers)
                if ('CompressionStream' in window) {
                    const compressionStream = new CompressionStream('gzip');
                    const compressedStream = new Response(arrayBuffer).body.pipeThrough(compressionStream);
                    const compressedArrayBuffer = await new Response(compressedStream).arrayBuffer();

                    // Create compressed file
                    const compressedFile = new File(
                        [compressedArrayBuffer],
                        file.name,
                        {
                            type: file.type,
                            lastModified: Date.now()
                        }
                    );

                    return compressedFile;
                } else {
                    // Fallback: Simple byte reduction for older browsers
                    const uint8Array = new Uint8Array(arrayBuffer);

                    // Simple compression: remove redundant bytes (basic approach)
                    const compressed = this.simpleCompress(uint8Array);

                    const compressedFile = new File(
                        [compressed],
                        file.name,
                        {
                            type: file.type,
                            lastModified: Date.now()
                        }
                    );

                    return compressedFile;
                }
            } catch (error) {
                // If compression fails, return original file
                console.error('Compression error:', error);
                return file;
            }
        },

        /**
         * Advanced multi-pass image compression to achieve target file size
         * @param {File} file - Original image file
         * @returns {Promise<File>} - Compressed image file
         */
         async compressImage(file) {
            return new Promise((resolve, reject) => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = async () => {
                    try {
                        let compressedFile;

                        if (this.compressionSettings.enableMultiPass && this.compressionSettings.aggressiveMode) {
                            // Use advanced multi-pass compression
                            compressedFile = await this.performMultiPassCompression(img, file, canvas, ctx);
                        } else {
                            // Use basic compression
                            compressedFile = await this.performBasicCompression(img, file, canvas, ctx);
                        }

                        resolve(compressedFile);
                    } catch (error) {
                        console.error('Compression error:', error);
                        resolve(file); // Return original file if compression fails
                    }
                };

                img.onerror = () => {
                    reject(new Error('Image loading failed'));
                };

                img.src = URL.createObjectURL(file);
            });
        },

        /**
         * Calculate smart initial dimensions based on original file size
         * Always maintain 1920x1080 or closest aspect ratio, don't reduce dimensions
         */
        calculateSmartDimensions(img, originalSize) {
            const maxWidth = this.compressionSettings.imageMaxWidth; // 1920
            const maxHeight = this.compressionSettings.imageMaxHeight; // 1080

            // Calculate aspect ratio
            const aspectRatio = img.width / img.height;

            let targetWidth = maxWidth;
            let targetHeight = maxHeight;

            // Maintain aspect ratio while fitting within 1920x1080
            if (aspectRatio > (maxWidth / maxHeight)) {
                // Image is wider than 16:9 - fit to width
                targetWidth = maxWidth;
                targetHeight = Math.floor(maxWidth / aspectRatio);
            } else {
                // Image is taller than 16:9 - fit to height
                targetHeight = maxHeight;
                targetWidth = Math.floor(maxHeight * aspectRatio);
            }

            // Always return full resolution - don't scale down based on file size
            // Compression will be achieved through quality reduction, not dimension reduction
            return {
                width: targetWidth,
                height: targetHeight
            };
        },

        /**
         * Perform multi-pass compression with iterative optimization
         */
        async performMultiPassCompression(img, originalFile, canvas, ctx) {
            const targetSize = this.compressionSettings.targetFileSize;
            const maxIterations = this.compressionSettings.maxIterations;
            const minQuality = this.compressionSettings.minQuality;

            let bestResult = null;
            let bestSize = Infinity;

            // Calculate smart initial dimensions based on file size
            let dimensions = this.calculateSmartDimensions(img, originalFile.size);
            let currentQuality = this.compressionSettings.imageQuality;

            console.log(`Starting multi-pass compression for ${originalFile.name}:`);
            console.log(`Original: ${(originalFile.size / 1024).toFixed(1)}KB, Target: ${(targetSize / 1024).toFixed(1)}KB`);

            for (let iteration = 0; iteration < maxIterations; iteration++) {
                // Set canvas dimensions
                canvas.width = dimensions.width;
                canvas.height = dimensions.height;

                // Enable high-quality image smoothing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Clear and draw image
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

                // Convert to blob
                const blob = await new Promise(resolve => {
                    canvas.toBlob(resolve, this.compressionSettings.imageFormat, currentQuality);
                });

                if (!blob) continue;

                const currentSize = blob.size;
                console.log(`Iteration ${iteration + 1}: ${dimensions.width}x${dimensions.height}, Q:${(currentQuality * 100).toFixed(0)}%, Size: ${(currentSize / 1024).toFixed(1)}KB`);

                // Keep track of best result
                if (currentSize < bestSize) {
                    bestSize = currentSize;
                    bestResult = new File([blob], originalFile.name, {
                        type: this.compressionSettings.imageFormat,
                        lastModified: Date.now()
                    });
                }

                // Check if we've reached our target
                if (currentSize <= targetSize) {
                    console.log(`✓ Target achieved in ${iteration + 1} iterations!`);
                    break;
                }

                // Reduce quality for next iteration
                currentQuality = Math.max(minQuality, currentQuality * 0.9);
            }

            console.log(`Final result: ${(bestSize / 1024).toFixed(1)}KB (${((originalFile.size - bestSize) / originalFile.size * 100).toFixed(1)}% reduction)`);
            return bestResult || originalFile;
        },

        /**
         * Perform basic compression (fallback method)
         */
        async performBasicCompression(img, originalFile, canvas, ctx) {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = this.calculateDimensions(
                img.width,
                img.height,
                this.compressionSettings.imageMaxWidth,
                this.compressionSettings.imageMaxHeight
            );

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw and compress image
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with compression
            const blob = await new Promise(resolve => {
                canvas.toBlob(
                    resolve,
                    this.compressionSettings.imageFormat,
                    this.compressionSettings.imageQuality
                );
            });

            if (!blob) {
                throw new Error('Canvas to Blob conversion failed');
            }

            // Create new File object with compressed data
            return new File([blob], originalFile.name, {
                type: this.compressionSettings.imageFormat,
                lastModified: Date.now()
            });
        },

        /**
         * Calculate new dimensions while maintaining aspect ratio
         * @param {number} originalWidth
         * @param {number} originalHeight
         * @param {number} maxWidth
         * @param {number} maxHeight
         * @returns {object} - New width and height
         */
        calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
            let width = originalWidth;
            let height = originalHeight;

            // Calculate scaling factor
            const widthRatio = maxWidth / originalWidth;
            const heightRatio = maxHeight / originalHeight;
            const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale

            width = Math.round(originalWidth * ratio);
            height = Math.round(originalHeight * ratio);

            return { width, height };
        },

        /**
         * Simple compression fallback for older browsers
         * @param {Uint8Array} data - Original data
         * @returns {Uint8Array} - Compressed data
         */
        simpleCompress(data) {
            // Simple run-length encoding for basic compression
            const compressed = [];
            let i = 0;

            while (i < data.length) {
                let count = 1;
                const current = data[i];

                // Count consecutive identical bytes
                while (i + count < data.length && data[i + count] === current && count < 255) {
                    count++;
                }

                if (count > 3) {
                    // Use run-length encoding for sequences > 3
                    compressed.push(255, count, current); // 255 as escape character
                } else {
                    // Keep original bytes for short sequences
                    for (let j = 0; j < count; j++) {
                        compressed.push(current);
                    }
                }

                i += count;
            }

            return new Uint8Array(compressed);
        },
        /**
         * Comprehensive video compression using MediaRecorder API
         * @param {File} file - Original video file
         * @returns {Promise<File>} - Compressed video file
         */
        async compressVideo(file) {
            this.isVideoCompressing = true;
            
            // Only compress very large files (>50MB)
            if (file.size <= 50 * 1024 * 1024) { 
                console.log('File size acceptable, skipping compression');
                this.isVideoCompressing = false;
                return file;
            }
            
            console.log(`Starting balanced video compression for ${file.name}: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
            
            return new Promise((resolve) => {
                const video = document.createElement('video');
                video.src = URL.createObjectURL(file);
                video.muted = true;
                video.playsInline = true;
                video.crossOrigin = 'anonymous';
                
                const timeoutId = setTimeout(() => {
                    console.warn('Video compression timeout, returning original file');
                    URL.revokeObjectURL(video.src);
                    this.isVideoCompressing = false;
                    resolve(file);
                }, 90000); // 90 second timeout for large files
                
                video.onloadedmetadata = async () => {
                    try {
                        clearTimeout(timeoutId);
                        
                        // Check video duration - skip very long videos
                        if (video.duration > 600) { // 10 minutes
                            console.warn('Video too long, using original file');
                            URL.revokeObjectURL(video.src);
                            this.isVideoCompressing = false;
                            resolve(file);
                            return;
                        }
                        
                        const compressedFile = await this.processBalancedCompression(video, file);
                        
                        URL.revokeObjectURL(video.src);
                        this.isVideoCompressing = false;
                        resolve(compressedFile);
                        
                    } catch (error) {
                        console.error('Video compression error:', error);
                        clearTimeout(timeoutId);
                        URL.revokeObjectURL(video.src);
                        this.isVideoCompressing = false;
                        resolve(file);
                    }
                };
                
                video.onerror = (error) => {
                    console.error('Video loading error:', error);
                    clearTimeout(timeoutId);
                    URL.revokeObjectURL(video.src);
                    this.isVideoCompressing = false;
                    resolve(file);
                };
                
                video.load();
            });
        },

        /**
         * Balanced compression that preserves video integrity
         */
        async processBalancedCompression(video, originalFile) {
            // Try HTML5 video re-encoding first (preserves audio and metadata)
            if (this.canUseVideoReEncoding()) {
                return await this.reEncodeVideo(video, originalFile);
            }
            
            // Fallback to canvas-based compression with better settings
            return await this.compressVideoCanvas(video, originalFile);
        },

        /**
         * Check if we can use video re-encoding (best quality preservation)
         */
        canUseVideoReEncoding() {
            return window.MediaRecorder && 
                MediaRecorder.isTypeSupported && 
                (MediaRecorder.isTypeSupported('video/mp4') || 
                    MediaRecorder.isTypeSupported('video/webm'));
        },

        /**
         * Re-encode video while preserving audio and metadata
         */
        async reEncodeVideo(video, originalFile) {
            console.log('Using video re-encoding to preserve audio and quality');
            
            // Create video element for MediaRecorder
            const canvas = document.createElement('canvas');
            const { width, height } = this.calculateBalancedDimensions(video.videoWidth, video.videoHeight, originalFile.size);
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            
            // Get video and audio streams
            let videoStream, audioStream;
            
            try {
                // Try to capture both video and audio
                if (canvas.captureStream) {
                    videoStream = canvas.captureStream(25); // 25 FPS for good quality
                    
                    // Try to get audio from original video (if supported)
                    if (video.captureStream) {
                        const originalStream = video.captureStream();
                        audioStream = originalStream.getAudioTracks();
                    }
                } else {
                    throw new Error('captureStream not supported');
                }
                
                // Combine video and audio streams
                const combinedStream = new MediaStream([
                    ...videoStream.getVideoTracks(),
                    ...(audioStream || [])
                ]);
                
                const mimeType = this.getBestVideoMimeType();
                const bitrate = this.calculateBalancedBitrate(width, height, originalFile.size);
                
                console.log(`Re-encoding: ${width}x${height}, bitrate: ${(bitrate/1000000).toFixed(1)}Mbps`);
                
                const mediaRecorder = new MediaRecorder(combinedStream, {
                    mimeType: mimeType,
                    videoBitsPerSecond: bitrate,
                    audioBitsPerSecond: 128000 // 128kbps audio
                });
                
                const chunks = [];
                
                return new Promise((resolve, reject) => {
                    let recordingStartTime = 0;
                    let expectedDuration = video.duration;
                    
                    mediaRecorder.ondataavailable = (event) => {
                        if (event.data.size > 0) {
                            chunks.push(event.data);
                        }
                    };
                    
                    mediaRecorder.onstart = () => {
                        recordingStartTime = performance.now();
                        console.log(`MediaRecorder started, expected duration: ${expectedDuration.toFixed(2)}s`);
                    };
                    
                    mediaRecorder.onstop = async () => {
                        const recordingDuration = (performance.now() - recordingStartTime) / 1000;
                        console.log(`MediaRecorder stopped after ${recordingDuration.toFixed(2)}s`);
                        
                        const compressedBlob = new Blob(chunks, { type: mimeType });
                        
                        // Check if compression was reasonable (don't over-compress)
                        const compressionRatio = compressedBlob.size / originalFile.size;
                        
                        if (compressionRatio < 0.05) { // Less than 5% of original size
                            console.warn('Compression too aggressive, using original file');
                            resolve(originalFile);
                            return;
                        }
                        
                        // Fix video timing metadata
                        const fixedBlob = await this.fixVideoMetadata(compressedBlob, video, mimeType);
                        
                        const compressedFile = new File([fixedBlob || compressedBlob], originalFile.name, {
                            type: mimeType,
                            lastModified: Date.now()
                        });
                        
                        const reduction = ((originalFile.size - compressedFile.size) / originalFile.size * 100).toFixed(1);
                        console.log(`Re-encoding result: ${(originalFile.size/1024/1024).toFixed(1)}MB → ${(compressedFile.size/1024/1024).toFixed(1)}MB (${reduction}% reduction)`);
                        
                        resolve(compressedFile);
                    };
                    
                    mediaRecorder.onerror = reject;
                    
                    // Use timeslice for better timing accuracy
                    mediaRecorder.start(100); // 100ms timeslices for better timing
                    
                    // Start rendering with preservation of quality
                    this.renderVideoPreserveQuality(video, ctx, width, height, () => {
                        console.log('Video rendering complete, stopping MediaRecorder...');
                        mediaRecorder.stop();
                    });
                });
                
            } catch (error) {
                console.warn('Video re-encoding failed, falling back to canvas compression:', error);
                return await this.compressVideoCanvas(video, originalFile);
            }
        },

        /**
         * Canvas-based compression with better quality settings
         */
        async compressVideoCanvas(video, originalFile) {
            console.log('Using canvas-based compression with quality preservation');
            
            const { width, height } = this.calculateBalancedDimensions(video.videoWidth, video.videoHeight, originalFile.size);
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d', {
                alpha: false,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high'
            });
            
            if (!canvas.captureStream) {
                // Fallback: create a single high-quality frame
                return await this.createVideoThumbnail(video, canvas, ctx, originalFile);
            }
            
            const stream = canvas.captureStream(24); // Higher FPS for better quality
            const mimeType = this.getBestVideoMimeType();
            const bitrate = this.calculateBalancedBitrate(width, height, originalFile.size);
            
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: mimeType,
                videoBitsPerSecond: bitrate
            });
            
            const chunks = [];
            
            return new Promise((resolve) => {
                mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        chunks.push(event.data);
                    }
                };
                
                mediaRecorder.onstop = () => {
                    const compressedBlob = new Blob(chunks, { type: mimeType });
                    
                    // Ensure reasonable compression ratio
                    const compressionRatio = compressedBlob.size / originalFile.size;
                    
                    if (compressionRatio < 0.1) { // Less than 10% is too aggressive
                        console.warn('Canvas compression too aggressive, using original');
                        resolve(originalFile);
                        return;
                    }
                    
                    const compressedFile = new File([compressedBlob], originalFile.name, {
                        type: mimeType,
                        lastModified: Date.now()
                    });
                    
                    const reduction = ((originalFile.size - compressedBlob.size) / originalFile.size * 100).toFixed(1);
                    console.log(`Canvas compression: ${(originalFile.size/1024/1024).toFixed(1)}MB → ${(compressedBlob.size/1024/1024).toFixed(1)}MB (${reduction}% reduction)`);
                    
                    resolve(compressedFile);
                };
                
                mediaRecorder.start(1000);
                
                this.renderVideoPreserveQuality(video, ctx, width, height, () => {
                    mediaRecorder.stop();
                });
            });
        },

        /**
         * Create high-quality video thumbnail as fallback
         */
        async createVideoThumbnail(video, canvas, ctx, originalFile) {
            console.log('Creating high-quality video thumbnail');
            
            // Seek to middle of video for best representative frame
            video.currentTime = video.duration / 2;
            
            return new Promise((resolve) => {
                const onSeeked = () => {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    
                    canvas.toBlob((blob) => {
                        const thumbnailFile = new File([blob], 
                            originalFile.name.replace(/\.[^/.]+$/, '.jpg'), {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        
                        console.log(`Video thumbnail: ${(originalFile.size/1024/1024).toFixed(1)}MB → ${(blob.size/1024).toFixed(1)}KB`);
                        resolve(thumbnailFile);
                        
                    }, 'image/jpeg', 0.85); // High quality JPEG
                    
                    video.removeEventListener('seeked', onSeeked);
                };
                
                video.addEventListener('seeked', onSeeked);
            });
        },

        /**
         * Calculate balanced dimensions that don't over-compress
         */
        calculateBalancedDimensions(originalWidth, originalHeight, fileSize) {
            const fileSizeMB = fileSize / (1024 * 1024);
            
            // More conservative dimension limits based on file size
            let maxWidth, maxHeight;
            
            if (fileSizeMB > 200) {
                maxWidth = 1280;  // For very large files
                maxHeight = 720;
            } else if (fileSizeMB > 100) {
                maxWidth = 1600;  // For large files  
                maxHeight = 900;
            } else {
                maxWidth = 1920;  // Preserve HD for smaller large files
                maxHeight = 1080;
            }
            
            // Don't upscale smaller videos
            if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
                return { width: originalWidth, height: originalHeight };
            }
            
            const aspectRatio = originalWidth / originalHeight;
            let width, height;
            
            if (aspectRatio > maxWidth / maxHeight) {
                width = maxWidth;
                height = Math.floor(maxWidth / aspectRatio);
            } else {
                height = maxHeight;
                width = Math.floor(maxHeight * aspectRatio);
            }
            
            // Ensure even dimensions
            width = width % 2 === 0 ? width : width - 1;
            height = height % 2 === 0 ? height : height - 1;
            
            return { 
                width: Math.max(width, 2), 
                height: Math.max(height, 2) 
            };
        },

        /**
         * Calculate balanced bitrate that preserves quality
         */
        calculateBalancedBitrate(width, height, originalFileSize) {
            const pixels = width * height;
            const fileSizeMB = originalFileSize / (1024 * 1024);
            
            // Base bitrate for good quality
            let baseBitrate = 2000000; // 2 Mbps for good quality
            
            // Adjust for resolution
            if (pixels <= 640 * 480) {
                baseBitrate = 1000000;      // 1 Mbps for SD
            } else if (pixels <= 1280 * 720) {
                baseBitrate = 1500000;      // 1.5 Mbps for 720p
            } else if (pixels <= 1920 * 1080) {
                baseBitrate = 2500000;      // 2.5 Mbps for 1080p
            } else {
                baseBitrate = 3000000;      // 3 Mbps for higher res
            }
            
            // Only reduce bitrate for extremely large files
            if (fileSizeMB > 300) {
                baseBitrate *= 0.7;  // 30% reduction for huge files
            } else if (fileSizeMB > 150) {
                baseBitrate *= 0.8;  // 20% reduction for very large files
            }
            
            return Math.max(baseBitrate, 800000); // Minimum 800kbps for watchable quality
        },

        /**
         * Render video with improved timing for proper progress bar functionality
         */
        renderVideoPreserveQuality(video, ctx, width, height, onComplete) {
            let frameCount = 0;
            const startTime = performance.now();
            const maxRenderTime = 120000; // 2 minutes for quality processing
            
            // Key improvement: Better time synchronization
            video.currentTime = 0;
            video.playbackRate = 1.0; // Use normal speed for better timing accuracy
            
            let lastFrameTime = 0;
            const targetFrameInterval = 1000 / 25; // 25 FPS = 40ms per frame
            
            const renderFrame = (currentTime) => {
                const elapsed = performance.now() - startTime;
                
                if (elapsed > maxRenderTime) {
                    console.warn('Quality rendering timeout');
                    onComplete();
                    return;
                }
                
                if (video.ended || video.error) {
                    console.log(`Quality rendering completed: ${frameCount} frames in ${(elapsed/1000).toFixed(1)}s`);
                    onComplete();
                    return;
                }
                
                // Throttle frame rendering for consistent timing
                if (currentTime - lastFrameTime >= targetFrameInterval) {
                    if (video.readyState >= 2) {
                        try {
                            // Clear canvas first for clean frames
                            ctx.clearRect(0, 0, width, height);
                            ctx.drawImage(video, 0, 0, width, height);
                            frameCount++;
                            
                            if (frameCount % 125 === 0) { // Log every 5 seconds of video
                                const progress = (video.currentTime / video.duration * 100).toFixed(1);
                                console.log(`Rendering progress: ${progress}% (${video.currentTime.toFixed(1)}s / ${video.duration.toFixed(1)}s)`);
                            }
                        } catch (error) {
                            console.warn('Frame render error:', error);
                        }
                    }
                    lastFrameTime = currentTime;
                }
                
                // Continue rendering if video is playing
                if (!video.ended && !video.paused) {
                    requestAnimationFrame(renderFrame);
                } else {
                    onComplete();
                }
            };
            
            // Ensure video loads and plays properly
            const startPlayback = () => {
                video.play()
                    .then(() => {
                        console.log(`Starting video render: ${video.duration.toFixed(2)}s duration, ${width}x${height} resolution`);
                        requestAnimationFrame(renderFrame);
                    })
                    .catch(error => {
                        console.error('Video play error:', error);
                        onComplete();
                    });
            };
            
            // Wait for video to be ready
            if (video.readyState >= 3) { // HAVE_FUTURE_DATA
                startPlayback();
            } else {
                video.oncanplay = startPlayback;
                video.oncanplaythrough = startPlayback;
            }
            
            // Fallback timeout for video loading
            setTimeout(() => {
                if (frameCount === 0) {
                    console.warn('Video failed to start rendering, forcing start');
                    startPlayback();
                }
            }, 3000);
        },

        /**
         * Fix video metadata to ensure proper progress bar functionality
         */
        async fixVideoMetadata(compressedBlob, originalVideo, mimeType) {
            try {
                // Create a temporary video element to test the compressed video
                const tempVideo = document.createElement('video');
                tempVideo.src = URL.createObjectURL(compressedBlob);
                tempVideo.muted = true;
                tempVideo.playsInline = true;
                
                return new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        URL.revokeObjectURL(tempVideo.src);
                        resolve(null); // Return null if can't fix metadata
                    }, 5000);
                    
                    tempVideo.onloadedmetadata = () => {
                        clearTimeout(timeout);
                        
                        // Check if duration is reasonable
                        if (tempVideo.duration > 0 && !isNaN(tempVideo.duration)) {
                            console.log(`Compressed video duration: ${tempVideo.duration.toFixed(2)}s (original: ${originalVideo.duration.toFixed(2)}s)`);
                            
                            // If duration is significantly different, try to create a new blob with proper headers
                            if (Math.abs(tempVideo.duration - originalVideo.duration) > 1) {
                                console.log('Duration mismatch detected, attempting to fix...');
                                
                                // For WebM files, we can try to adjust the metadata
                                if (mimeType.includes('webm')) {
                                    this.fixWebMMetadata(compressedBlob, originalVideo.duration).then(resolve);
                                } else {
                                    URL.revokeObjectURL(tempVideo.src);
                                    resolve(null);
                                }
                            } else {
                                URL.revokeObjectURL(tempVideo.src);
                                resolve(compressedBlob); // Video is fine as-is
                            }
                        } else {
                            console.warn('Invalid duration in compressed video');
                            URL.revokeObjectURL(tempVideo.src);
                            resolve(null);
                        }
                    };
                    
                    tempVideo.onerror = () => {
                        clearTimeout(timeout);
                        URL.revokeObjectURL(tempVideo.src);
                        resolve(null);
                    };
                    
                    tempVideo.load();
                });
                
            } catch (error) {
                console.warn('Could not fix video metadata:', error);
                return null;
            }
        },

        /**
         * Fix WebM metadata for proper seeking
         */
        async fixWebMMetadata(blob, originalDuration) {
            try {
                // This is a simplified metadata fix for WebM
                // In a production environment, you might want to use a library like webm-writer
                
                const arrayBuffer = await blob.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);
                
                // For now, we'll return the original blob since WebM metadata manipulation
                // requires complex binary parsing. The main fix is in the rendering process.
                return blob;
                
            } catch (error) {
                console.warn('WebM metadata fix failed:', error);
                return blob;
            }
        },
        getBestVideoMimeType() {
            const types = [
                'video/webm;codecs=vp9,opus',   // VP9 with audio
                'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // H.264 with audio
                'video/webm;codecs=vp8,opus',   // VP8 with audio
                'video/webm',
                'video/mp4'
            ];
            
            if (!MediaRecorder.isTypeSupported) {
                return 'video/webm';
            }
            
            for (const type of types) {
                if (MediaRecorder.isTypeSupported(type)) {
                    return type;
                }
            }
            
            return 'video/webm';
        }
	}
};
</script>

  <style scoped>
  .image-uploader {
	width: 100%;
  }

  .drop-zone {
	min-height: 200px;
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
  }

  .drag-over {
	border-color: #0d6efd;
	background-color: rgba(13, 110, 253, 0.05);
  }

  .upload-icon-container {
	width: 80px;
	height: 80px;
	margin: 0 auto;
	background-color: rgba(13, 110, 253, 0.1);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
  }

  .upload-icon-container i {
	font-size: 2.5rem;
	color: #0d6efd;
  }

  .image-wrapper {
	position: relative;
	padding-top: 100%; /* 1:1 Aspect Ratio */
	/* overflow: hidden; */
	box-shadow: 0 3px 6px rgba(0,0,0,0.1);
  }

  .preview-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .preview-item {
	border-radius: 8px;
	/* overflow: hidden; */
	transition: transform 0.2s;
  }

  .preview-item:hover {
	transform: translateY(-3px);
  }

  .preview-info {
	background-color: white;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
  }

  .add-more {
	border: 2px dashed #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	min-height: 100px;
  }

  .add-more:hover {
	border-color: #0d6efd;
	background-color: rgba(13, 110, 253, 0.05);
  }

  /* Gallery styling */
  .gallery-item {
	position: relative;
	border-radius: 8px;
	overflow: hidden;
	height: 0;
	padding-top: 100%; /* 1:1 Aspect Ratio */
	box-shadow: 0 4px 8px rgba(0,0,0,0.1);
	transition: transform 0.2s;
  }

  .gallery-item:hover {
	transform: translateY(-3px);
  }

  .gallery-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .gallery-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 50%);
	opacity: 0;
	transition: opacity 0.3s;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 10px;
  }

  .show-controls {
	opacity: 1;
  }

  .gallery-info {
	color: white;
	margin-top: auto;
	font-size: 0.8rem;
  }

  .gallery-actions {
	display: flex;
	gap: 8px;
	margin-top: 8px;
	justify-content: flex-end;
  }

  .quick-action-btn {
	opacity: 0.5;
	transition: opacity 0.2s;
  }

  .gallery-item:hover .quick-action-btn {
	opacity: 1;
  }

  /* Toast notification */
  .toast.show {
	display: block;
	opacity: 1;
  }

  .toast {
	transition: opacity 0.3s ease;
	display: none;
	opacity: 0;
  }

  /* Add fade-in animation for new files */
  .preview-item {
	animation: fadeIn 0.3s;
  }

  @keyframes fadeIn {
	from { opacity: 0; transform: translateY(10px); }
	to { opacity: 1; transform: translateY(0); }
  }

  .file-progress-card {
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
	transition: transform 0.2s;
  }

  .file-progress-card:hover {
	transform: translateY(-3px);
  }

  .image-preview {
	position: relative;
	height: 120px;
	overflow: hidden;
	border-radius: 4px;
  }

  .image-preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
  }

  .progress-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0,0,0,0.3);
	color: white;
	font-weight: bold;
	font-size: 1.2rem;
  }

  .progress-percentage {
	background-color: rgba(0,0,0,0.5);
	padding: 4px 10px;
	border-radius: 20px;
  }
</style>
